// frontend/src/pages/AssignPoints/HousePointFlow.js
// Confirm & award points to ALL students in a selected house.
// - Resolves habit/reason by name if IDs aren't provided
// - Fetches house members via studentsApi.list({ house: <name> })
// - Awards using bulk endpoint if available, else falls back to per-student POST
// - Refreshes leaderboard and routes to /dashboard on success
//
// Backend refactor notes:
// - Habits/Reasons are now served via GET /api/catalog/
// - Award endpoints are now:
//     POST /api/award/
//     POST /api/award-bulk/
// - Django/DRF expects trailing slashes for POSTs.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { studentsApi } from "../../api/studentsApi";

function HousePointFlow() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const house_name = sp.get("house_name") || "";
  const habit_name = sp.get("habit_name") || sp.get("habit") || "";
  const reason_text = sp.get("reason_text") || sp.get("reason") || "";
  const points_in = sp.get("points") || "1";

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [habitId, setHabitId] = useState(sp.get("habit_id") || "");
  const [reasonId, setReasonId] = useState(sp.get("reason_id") || "");

  const points = useMemo(() => Number(points_in) || 1, [points_in]);

  const summary = useMemo(
    () => [
      ["Target", house_name ? `House: ${house_name}` : "—"],
      ["Habit", habit_name || (habitId ? `#${habitId}` : "—")],
      ["Reason", reason_text || (reasonId ? `#${reasonId}` : "—")],
      ["Points", String(points)],
    ],
    [house_name, habit_name, reason_text, habitId, reasonId, points]
  );

  // Helper: normalize catalog habits array from backend shapes
  function extractHabitsFromCatalogData(data) {
    // Our backend returns { ok: true, habits: [...] }.
    // But we handle other plausible shapes defensively.
    if (!data) return [];
    if (Array.isArray(data.habits)) return data.habits;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  }

  // Resolve habit & reason IDs if only names were provided
  useEffect(() => {
    let alive = true;
    (async () => {
      // If both IDs present, nothing to do
      if (habitId && reasonId) return;

      // If we don't have names either, nothing to resolve
      if (!habit_name && !reason_text) return;

      setBusy(true);
      try {
        // Load catalog once
        const catRes = await api.get("/api/catalog/");
        if (!catRes.ok) throw new Error("Catalog unavailable.");

        const habits = extractHabitsFromCatalogData(catRes.data);

        // Resolve habit id by habit_name
        let hid = habitId;
        let habitObj = null;

        if (!hid && habit_name) {
          const hit =
            habits.find(h => (h.name || h.title) === habit_name) ||
            habits.find(h => String(h.id) === String(habit_name)) ||
            habits[0];

          hid = hit?.id ? String(hit.id) : "";
          habitObj = hit || null;
        } else if (hid) {
          habitObj = habits.find(h => String(h.id) === String(hid)) || null;
        }

        // Resolve reason id within the chosen habit
        let rid = reasonId;
        if (!rid && reason_text) {
          const reasons =
            (habitObj && (habitObj.reasons || habitObj.reason_options || habitObj.reasonChoices)) ||
            [];

          // Reasons might be objects with {id, text} or {id, name}
          const rHit =
            reasons.find(r => (r.text || r.name || r.title) === reason_text) ||
            reasons.find(r => String(r.id) === String(reason_text)) ||
            null;

          rid = rHit?.id ? String(rHit.id) : "";
        }

        if (alive) {
          setHabitId(hid || "");
          setReasonId(rid || "");
        }
      } catch {
        if (alive) setErr("Failed to resolve habit or reason.");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habit_name, reason_text]); // changes to names should re-resolve

  async function awardToIds(ids, hid, rid) {
    // The refactored backend expects:
    //   POST /api/award-bulk/ with { items: [...] }
    // and /api/award/ for single awards.

    const items = ids.map(id => ({
      target_type: "student",
      student_id: String(id),
      habit_id: String(hid),
      reason_id: String(rid),
      points,
    }));

    // Try bulk endpoint first (preferred)
    const bulk = await api.post("/api/award-bulk/", { items });
    if (bulk && bulk.ok) return;

    // Fall back to one-by-one
    for (const p of items) {
      const r = await api.post("/api/award/", p);
      if (!r.ok) throw new Error("One or more awards failed.");
    }
  }

  async function onConfirm() {
    setErr("");
    if (!house_name) return setErr("Missing house.");
    if (!(habitId || habit_name)) return setErr("Pick a habit.");
    if (!(reasonId || reason_text)) return setErr("Pick a reason.");

    setBusy(true);
    try {
      // Fetch students in the house
      const list = await studentsApi.list({ house: house_name });
      if (!list.ok) throw new Error("Could not load students for this house.");

      const ids = (list.students || []).map(s => s.id).filter(Boolean);
      if (ids.length === 0) throw new Error("No students in this house.");

      const hid = habitId;
      const rid = reasonId;
      if (!hid || !rid) throw new Error("Habit or Reason not resolved.");

      // Post awards
      await awardToIds(ids, hid, rid);

      // Refresh leaderboard and return to dashboard
      await api.get("/api/leaderboard/").catch(() => {});
      navigate("/dashboard");
    } catch (e) {
      setErr(e?.message || "Award failed.");
    } finally {
      setBusy(false);
    }
  }

  const disabled = busy || !house_name || !(habitId || habit_name) || !(reasonId || reason_text);

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Confirm &amp; Award Points (House)</h1>

      <div style={s.card}>
        {summary.map(([k, v]) => (
          <div key={k} style={s.rowBetween}>
            <div style={s.label}>{k}</div>
            <div>{v}</div>
          </div>
        ))}
      </div>

      {err && <div style={s.error}>{err}</div>}

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={() => navigate(-1)} style={s.btnSecondary}>Back</button>
        <button onClick={onConfirm} disabled={disabled} style={s.btnPrimary}>
          {busy ? "Awarding…" : "Award Points to House"}
        </button>
      </div>
    </div>
  );
}

const s = {
  page: { padding: 20 },
  h1: { margin: "10px 0 16px", fontSize: 24, color: "#003e73" },
  card: {
    display: "grid",
    gap: 6,
    padding: 14,
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.1)",
    background: "#fff",
    marginBottom: 12,
  },
  rowBetween: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  label: { fontWeight: 700, color: "#003e73" },
  btnPrimary: {
    height: 40, padding: "0 16px", borderRadius: 8, border: "none",
    background: "#2e7d32", color: "#fff", cursor: "pointer",
  },
  btnSecondary: {
    height: 40, padding: "0 16px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.2)",
    background: "#fff", color: "#333", cursor: "pointer",
  },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
  },
};

export default HousePointFlow;
